import { useCallback } from 'react';
import { sessionRole } from '../../authProvider';
import { useCaretakerPreference } from '../fleet/myVehicles';
import { useFeature } from '../featureFlags/FeatureGate';

/**
 * Warianty planisty FTL tylko przy jego autach (07.10.2026) — lustro reguły
 * z API (`apps/api/src/user-offers/variant-scope.ts`).
 *
 * Odpowiedź Marathonu z 07.10.2026: „warianty mogą dowolnie dodawać
 * i odejmować wszyscy planiści — w obszarze zbioru własnych pojazdów”.
 * „Własne” = auta, których opiekunem FTL (`Car.ftlCaretaker`) jest ten,
 * kogo planista wskazał w „Jestem opiekunem FTL”. Planista bez wskazania
 * działa bez zawężenia; planista główny i administracja — zawsze bez.
 *
 * Front **ukrywa** „Wariant” i „Usuń wariant” przy cudzych autach planisty
 * (funkcje bez uprawnień są ukryte, nie wyszarzone — omówienie 01.10), ale
 * o dostępie rozstrzyga API (403 z powodem). Zgodność listy ról z API
 * pilnuje `variantScope.test.ts`.
 *
 * Za przełącznikiem `variantScopeOwnVehicles` (decyzja użytkownika
 * 12.10.2026, wersja „obecna / nowa”): wyłączony — przyciski przy każdym
 * aucie, jak w 0.31.3.
 */

/** Klucz przełącznika funkcji — jak w API (`feature-flags.ts`). */
export const VARIANT_SCOPE_FEATURE = 'variantScopeOwnVehicles';

/** Role ograniczone do własnych aut — jak `VARIANT_SCOPE_ROLES` w API. */
export const VARIANT_SCOPE_ROLES: readonly string[] = ['PLANNER_FTL'];

const clean = (v: string | null | undefined): string | null =>
  typeof v === 'string' && v.trim() !== '' ? v.trim() : null;

/** Czy ta rola z tym wskazanym opiekunem może dodać/zwolnić wariant auta. */
export function canManageVariant(
  role: string | null | undefined,
  userCaretaker: string | null | undefined,
  carCaretaker: string | null | undefined,
): boolean {
  if (!role || !VARIANT_SCOPE_ROLES.includes(role)) return true;
  const mine = clean(userCaretaker);
  if (mine === null) return true;
  return clean(carCaretaker) === mine;
}

/**
 * Sprawdzenie dla zalogowanego: rola z tokenu sesji, opiekun z preferencji
 * („Jestem opiekunem FTL”, odświeżany po zmianie w menu). Do czasu odczytu
 * preferencji planista nie widzi przycisków — lepiej chwilę bez przycisku
 * niż przycisk, który skończy się odmową.
 */
export function useVariantScope(): (carCaretaker: string | null | undefined) => boolean {
  const role = sessionRole();
  const on = useFeature(VARIANT_SCOPE_FEATURE);
  // Opiekuna z konta pytamy tylko dla roli ograniczonej — reszta i tak
  // dodaje warianty wszystkich aut.
  const restricted = on && !!role && VARIANT_SCOPE_ROLES.includes(role);
  const { caretaker, loaded } = useCaretakerPreference(restricted);
  return useCallback(
    (carCaretaker: string | null | undefined) => {
      if (!restricted) return true;
      if (!loaded) return false;
      return canManageVariant(role, caretaker, carCaretaker);
    },
    [restricted, role, loaded, caretaker],
  );
}

/**
 * Dopisek do komunikatu po dodaniu/zwolnieniu wariantu: API mówi
 * (`variantScopeNote`), że planista bez wskazanego opiekuna działa bez
 * zawężenia — żeby nie wziął tego za uprawnienie.
 */
export function withVariantScopeNote(message: string, response: unknown): string {
  const note =
    response && typeof response === 'object' && 'variantScopeNote' in response
      ? (response as { variantScopeNote?: unknown }).variantScopeNote
      : null;
  if (typeof note !== 'string' || note.trim() === '') return message;
  return /[.!?]$/.test(message) ? `${message} ${note}` : `${message}. ${note}`;
}
